import React, { useEffect, useState } from "react";
import {
  Table,
  Avatar,
  Tag,
  Popconfirm,
  message,
  ConfigProvider,
  Tooltip,
  Modal,
  Image,
  Empty,
  Spin,
  Button,
  Space,
} from "antd";
import {
  DeleteOutlined,
  ShopOutlined,
  MailOutlined,
  PhoneOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "./shops.css";
import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";

const ShopList = () => {
  const [shops, setShops] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsModalOpen, setProductsModalOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopProducts, setShopProducts] = useState([]);

  const getAllShops = () => {
    let getRes = (res) => {
      console.log("res of getting shops", res);
      setShops(res?.data || []);
    };

    callApi(
      "GET",
      routes.getAllShops, // Ensure this route exists
      null,
      setIsLoading,
      getRes,
      (error) => {
        console.log("error", error);
        message.error("Failed to load shops");
      }
    );
  };

  useEffect(() => {
    getAllShops();
  }, []);

  const handleDelete = (id) => {
    console.log("Delete Shop ID:", id);
    message.success("Shop deleted successfully");
    // Re-fetch or filter local state
  };

  const getVendorProducts = (record) => {
    setSelectedShop(record);
    setProductsModalOpen(true);
    setShopProducts([]);

    let getRes = (res) => {
      const vendorDetails = res?.data || {};
      setShopProducts(
        vendorDetails?.products ||
          vendorDetails?.vendor?.products ||
          vendorDetails?.vendorProducts ||
          []
      );
    };

    callApi(
      "GET",
      `${routes.getAdminVendorById}/${record._id}`,
      null,
      setProductsLoading,
      getRes,
      (error) => {
        console.log("error fetching vendor products", error);
        message.error("Failed to fetch shop products");
      }
    );
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "NGN",
      maximumFractionDigits: 0,
    }).format(price || 0);

  const renderProductsModalContent = () => {
    if (productsLoading) {
      return (
        <div className="shop-products-loading">
          <Spin />
        </div>
      );
    }

    if (!shopProducts.length) {
      return <Empty description="No products found for this shop" />;
    }

    return (
      <div className="shop-products-list">
        {shopProducts.map((product) => (
          <div className="shop-product-card" key={product._id}>
            <Image
              src={product.productCoverImage}
              fallback="https://placehold.co/120x120?text=No+Image"
              className="shop-product-image"
              preview={!!product.productCoverImage}
            />
            <div className="shop-product-content">
              <div className="shop-product-header">
                <div>
                  <h3 className="shop-product-name">
                    {product.productName || "Unnamed Product"}
                  </h3>
                  <p className="shop-product-details">
                    {product.productDetails || "No product details available"}
                  </p>
                </div>
                <Tag color={product.isActive ? "green" : "default"}>
                  {product.isActive ? "Active" : "Inactive"}
                </Tag>
              </div>

              <div className="shop-product-meta">
                <div>
                  <span className="shop-product-label">Price</span>
                  <strong>{formatPrice(product.productPrice)}</strong>
                </div>
                <div>
                  <span className="shop-product-label">Quantity</span>
                  <strong>{product.productQuantity ?? 0}</strong>
                </div>
                <div>
                  <span className="shop-product-label">Variants</span>
                  <strong>{product.variants?.length || 0}</strong>
                </div>
              </div>

              {product.variants?.length > 0 && (
                <div className="shop-product-variants">
                  {product.variants.map((variant) => (
                    <Tag key={variant._id}>
                      {variant.size || "Size"} / {variant.color || "Color"} -{" "}
                      {variant.quantity ?? 0}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: "Store Name",
      dataIndex: "storeName",
      key: "storeName",
      fixed: "left", // Keeps store name visible while scrolling horizontally
      render: (text, record) => (
        <div className="shop-info-cell">
          <Avatar
            src={record.avatar}
            shape="square"
            size="large"
            icon={<ShopOutlined />}
            className="shop-avatar"
          />
          <span className="store-name">{text || "Unnamed Store"}</span>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "storeCategory",
      key: "storeCategory",
      render: (category) => <Tag color="orange">{category || "General"}</Tag>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <span>
          <MailOutlined style={{ marginRight: 8, color: "#8c8c8c" }} />
          {email}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phone) => (
        <span>
          <PhoneOutlined style={{ marginRight: 8, color: "#8c8c8c" }} />
          {phone}
        </span>
      ),
    },
    {
      title: "Description",
      dataIndex: "storeDescription",
      key: "storeDescription",
      responsive: ["lg"], // Hidden on tablets/mobile to save space
      render: (desc) => (
        <Tooltip title={desc}>
          <div className="description-cell">{desc || "No description"}</div>
        </Tooltip>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      responsive: ["md"],
      render: (date) => dayjs(date).format("DD MMM YYYY"),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right", // Keeps delete button visible
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Products">
            <Button
              type="text"
              icon={<AppstoreOutlined />}
              className="products-action"
              onClick={() => getVendorProducts(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Shop?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <DeleteOutlined className="delete-action" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FFA450",
          fontFamily: "Roboto, sans-serif",
        },
      }}
    >
      <div className="user-table-container">
        <h2 style={{ marginBottom: 20 }}>Registered Shops</h2>
        <Table
          columns={columns}
          dataSource={shops}
          loading={isLoading}
          rowKey="_id"
          scroll={{ x: 1200 }} // Enables horizontal scroll for many columns
          pagination={{ pageSize: 10 }}
        />
        <Modal
          title={`${selectedShop?.storeName || "Shop"} Products`}
          open={productsModalOpen}
          onCancel={() => setProductsModalOpen(false)}
          footer={null}
          width={900}
          destroyOnClose
        >
          {renderProductsModalContent()}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default ShopList;
